import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type {
    ConnectionAlertDto,
    ConnectionAlertsDto,
} from "@station/data/dto/connection-alert.dto";
import {
    ConnectionAlertDtoSchema,
    ConnectionAlertsDtoSchema,
} from "@station/data/dto/connection-alert.dto";
import {
    ConnectionAlert,
    ConnectionAlerts,
} from "@station/domain/models/connection-alert.model";
import { injectable } from "inversify";
import { err, Result } from "neverthrow";

@injectable()
export class ConnectionAlertMapper {
    map(
        connectionAlertDto: ConnectionAlertDto,
    ): Result<ConnectionAlert, ModelError<typeof ConnectionAlert>> {
        const connectionAlertDtoParsed =
            ConnectionAlertDtoSchema.safeParse(connectionAlertDto);

        if (!connectionAlertDtoParsed.success) {
            console.error(connectionAlertDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return ConnectionAlert.createWithRecovery({
            id: connectionAlertDto.id,
            hoursElapsedToTrigger: connectionAlertDto.hours_elapsed_to_trigger,
            notifyAll: connectionAlertDto.notify_all,
            stationIds: connectionAlertDto.station_ids,
            stations: connectionAlertDto.stations,
            isEnabled: connectionAlertDto.is_enabled ?? false,
        });
    }

    mapList(connectionAlertsDto: ConnectionAlertsDto): ConnectionAlerts {
        const connectionAlertsDtoParsed =
            ConnectionAlertsDtoSchema.safeParse(connectionAlertsDto);

        if (!connectionAlertsDtoParsed.success) {
            console.error(connectionAlertsDtoParsed.error.issues);
            return ConnectionAlerts.empty();
        }

        const connectionAlerts = connectionAlertsDto.mapNotNull(
            (connectionAlertDto) =>
                this.map(connectionAlertDto).match(
                    (connectionAlert) => connectionAlert,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping connection alerts:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
        );

        return ConnectionAlerts.create({
            connectionAlerts,
            count: connectionAlerts.length,
        }).unwrapOr(ConnectionAlerts.empty());
    }
}
